import { useTranslation } from "react-i18next";
import { Switch } from "components/ui/switch";
import { useEffect, useState } from "react";
import { useKnowledgeBase, useUpdateReferenceSetting, useUpdateKnowledgeBase } from "features/profile/hooks/use-knowledge-base";
import { MemoryDialog } from "../../memory-dialog/memory-dialog";
import { Input } from "components/ui/input";
import { Textarea } from "components/ui/textarea";
import { Label } from "components/ui/label";
import { Button } from "components/ui/button";
import { Loader2 } from "lucide-react";
import { useToast } from "hooks/use-toast";
import { KnowledgeBaseFormData } from "features/profile/types/knowledge-base";

interface PersonalizationTabProps {
    tabId: string;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const PersonalizationTab = ({ tabId }: PersonalizationTabProps) => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const [enableChatHistory, setEnableChatHistory] = useState<boolean>(false);
    const [enableSavedMemories, setEnableSavedMemories] = useState<boolean>(false);
    const [isMemoryDialogOpen, setIsMemoryDialogOpen] = useState<boolean>(false);
    const { data: knowledgeBaseData, isLoading: isLoadingData } = useKnowledgeBase();
    const { mutateAsync: updateReferenceSetting } = useUpdateReferenceSetting();
    const updateKnowledgeBase = useUpdateKnowledgeBase();

    // Knowledge Base Form state
    const [formData, setFormData] = useState<KnowledgeBaseFormData>({
        user_nickname: '',
        user_profession: '',
        llm_personality: '',
        description: '',
        is_enabled: true
    });
    const [hasChanges, setHasChanges] = useState(false);
    const [enableForNewChat, setEnableForNewChat] = useState(true);

    const handleSettingChange = async (type: 'chatHistory' | 'savedMemories', checked: boolean) => {
        // console.log('handleSettingChange', type, checked);
        const prevValue = type === 'chatHistory' ? enableChatHistory : enableSavedMemories;
        const setter = type === 'chatHistory' ? setEnableChatHistory : setEnableSavedMemories;
        const setting = type === 'chatHistory' ? 'reference_chat_history' : 'reference_saved_memories';
        const errorMessage = type === 'chatHistory' ? 'reference chat history' : 'reference saved memories';

        try {
            setter(checked);
            await updateReferenceSetting({ setting, value: checked });
        } catch (error) {
            console.error(`Failed to update ${errorMessage}:`, error, errorMessage);
            setter(prevValue);
        }
    };

    // Initialize form data when knowledge base data is loaded
    useEffect(() => {
        if (knowledgeBaseData) {
            setEnableChatHistory(knowledgeBaseData.reference_chat_history || false);
            setEnableSavedMemories(knowledgeBaseData.reference_saved_memories || false);

            // Initialize knowledge base form
            const initialData = {
                user_nickname: knowledgeBaseData.user_nickname || '',
                user_profession: knowledgeBaseData.user_profession || '',
                llm_personality: knowledgeBaseData.llm_personality || '',
                description: knowledgeBaseData.description || '',
                is_enabled: knowledgeBaseData.is_enabled ?? true
            };
            setFormData(initialData);
            setEnableForNewChat(knowledgeBaseData.is_enabled ?? true);
        }
        setHasChanges(false);
    }, [knowledgeBaseData]);

    // Handle cancel - reset form to original values
    const handleCancel = () => {
        if (knowledgeBaseData) {
            setFormData({
                user_nickname: knowledgeBaseData.user_nickname || '',
                user_profession: knowledgeBaseData.user_profession || '',
                llm_personality: knowledgeBaseData.llm_personality || '',
                description: knowledgeBaseData.description || '',
                is_enabled: knowledgeBaseData.is_enabled ?? true
            });
            setEnableForNewChat(knowledgeBaseData.is_enabled ?? true);
        } else {
            setFormData({
                user_nickname: '',
                user_profession: '',
                llm_personality: '',
                description: '',
                is_enabled: true
            });
            setEnableForNewChat(true);
        }
        setHasChanges(false);
    };

    // Check if form data matches original data
    const checkIfChanged = (currentData: KnowledgeBaseFormData) => {
        if (!knowledgeBaseData) {
            // If no original data, check if any field is filled
            return currentData.user_nickname !== '' ||
                currentData.user_profession !== '' ||
                currentData.llm_personality !== '' ||
                currentData.description !== '' ||
                currentData.is_enabled !== true;
        }

        // Compare with original data
        return currentData.user_nickname !== (knowledgeBaseData.user_nickname || '') ||
            currentData.user_profession !== (knowledgeBaseData.user_profession || '') ||
            currentData.llm_personality !== (knowledgeBaseData.llm_personality || '') ||
            currentData.description !== (knowledgeBaseData.description || '') ||
            currentData.is_enabled !== (knowledgeBaseData.is_enabled ?? true);
    };

    // Handle form field changes
    const handleInputChange = (field: keyof KnowledgeBaseFormData, value: string) => {
        const newData = {
            ...formData,
            [field]: value
        };
        setFormData(newData);
        setHasChanges(checkIfChanged(newData));
    };

    // Handle form submission
    const handleSave = async () => {
        try {
            const result = await updateKnowledgeBase.mutateAsync(formData);

            if (result) {
                toast({
                    variant: 'success',
                    title: t('SUCCESS'),
                    description: t('KNOWLEDGE_BASE_UPDATE_SUCCESS')
                });
                setHasChanges(false);
            } else {
                toast({
                    variant: 'destructive',
                    title: t('ERROR'),
                    description: t('KNOWLEDGE_BASE_UPDATE_ERROR')
                });
            }
        } catch (error) {
            console.error('Failed to update knowledge base:', error);
            toast({
                variant: 'destructive',
                title: t('ERROR'),
                description: t('KNOWLEDGE_BASE_UPDATE_ERROR')
            });
        }
    };

    const isLoading = updateKnowledgeBase.isPending;
    const isSaveDisabled = !hasChanges || isLoading;

    return (
        <div className="h-full overflow-y-auto">
            <div className="px-4 sm:px-6 md:px-8 py-4 sm:py-5 space-y-6 sm:space-y-8 max-w-4xl">
                {/* Custom Knowledge Base Section */}
                <div className="space-y-4">
                    <div>
                        <h2 className="text-lg sm:text-xl font-semibold text-foreground mb-1">
                            {t('CUSTOM_KNOWLEDGE_BASE') || 'Custom Knowledge Base'}
                        </h2>
                        <p className="text-xs sm:text-sm text-muted-foreground">
                            {t('KNOWLEDGE_BASE_DESCRIPTION') || 'Add personalized responses.'}
                        </p>
                    </div>

                    {isLoadingData ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="h-6 w-6 animate-spin" />
                            <span className="ml-2">{t('LOADING')}</span>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {/* User Nickname Field */}
                            <div className="space-y-1.5 sm:space-y-2">
                                <Label htmlFor="userNickname" className="text-xs sm:text-sm font-medium">
                                    {t('KNOWLEDGE_BASE_NICKNAME_LABEL') || 'What should SeliseAI call you?'}
                                </Label>
                                <Input
                                    id="userNickname"
                                    disabled={!enableForNewChat}
                                    value={formData.user_nickname}
                                    onChange={(e) => handleInputChange('user_nickname', e.target.value)}
                                    placeholder={t('KNOWLEDGE_BASE_PLACEHOLDER') || 'Luca Meier'}
                                    maxLength={50}
                                    className="h-9 sm:h-10 text-sm"
                                />
                            </div>

                            {/* User Profession Field */}
                            <div className="space-y-1.5 sm:space-y-2">
                                <Label htmlFor="userProfession" className="text-xs sm:text-sm font-medium">
                                    {t('KNOWLEDGE_BASE_PROFESSION_LABEL') || 'What is your profession?'}
                                </Label>
                                <Input
                                    disabled={!enableForNewChat}
                                    id="userProfession"
                                    value={formData.user_profession}
                                    onChange={(e) => handleInputChange('user_profession', e.target.value)}
                                    placeholder={t('KNOWLEDGE_BASE_PLACEHOLDER') || 'Write here'}
                                    maxLength={100}
                                    className="h-9 sm:h-10 text-sm"
                                />
                            </div>

                            {/* LLM Personality Field */}
                            <div className="space-y-1.5 sm:space-y-2">
                                <Label htmlFor="llmPersonality" className="text-xs sm:text-sm font-medium">
                                    {t('KNOWLEDGE_BASE_PERSONALITY_LABEL') || 'What trait should SeliseAI have?'}
                                </Label>
                                <Textarea
                                    id="llmPersonality"
                                    disabled={!enableForNewChat}
                                    value={formData.llm_personality}
                                    onChange={(e) => handleInputChange('llm_personality', e.target.value)}
                                    placeholder={t('KNOWLEDGE_BASE_PLACEHOLDER') || 'Write here'}
                                    rows={3}
                                    maxLength={300}
                                    className="resize-none text-sm"
                                />
                            </div>

                            {/* Description Field */}
                            <div className="space-y-1.5 sm:space-y-2">
                                <Label htmlFor="description" className="text-xs sm:text-sm font-medium">
                                    {t('KNOWLEDGE_BASE_DESCRIPTION_LABEL') || 'Anything else SeliseAI should know about you?'}
                                </Label>
                                <Textarea
                                    id="description"
                                    disabled={!enableForNewChat}
                                    value={formData.description}
                                    onChange={(e) => handleInputChange('description', e.target.value)}
                                    placeholder={t('KNOWLEDGE_BASE_PLACEHOLDER') || 'Write here'}
                                    rows={4}
                                    maxLength={500}
                                    className="resize-none text-sm"
                                />
                            </div>

                            {/* Enable for new chat toggle */}
                            <div className="flex items-center justify-between">
                                <Label htmlFor="enableForNewChat" className="text-xs sm:text-sm font-medium">
                                    {t('KNOWLEDGE_BASE_ENABLE_FOR_NEW_CHAT') || 'Enable for new chat'}
                                </Label>
                                <Switch
                                    id="enableForNewChat"
                                    checked={enableForNewChat}
                                    className="w-[38px] sm:w-[42px] shrink-0"
                                    onCheckedChange={(checked) => {
                                        const newData = {
                                            ...formData,
                                            is_enabled: checked
                                        };
                                        setFormData(newData);
                                        setEnableForNewChat(checked);
                                        setHasChanges(checkIfChanged(newData));
                                    }}
                                />
                            </div>

                            {/* Action Buttons - Only show when there are changes */}
                            {hasChanges && (
                                <div className="flex justify-end gap-2 sm:gap-3 pt-2">
                                    <Button
                                        variant="outline"
                                        onClick={handleCancel}
                                        disabled={isLoading}
                                        className="w-20 sm:w-24 text-xs sm:text-sm h-8 sm:h-10"
                                    >
                                        {t('CANCEL') || 'Cancel'}
                                    </Button>
                                    <Button
                                        onClick={handleSave}
                                        disabled={isSaveDisabled}
                                        className="w-20 sm:w-24 text-xs sm:text-sm h-8 sm:h-10"
                                    >
                                        {isLoading ? (
                                            <>
                                                <Loader2 className="h-3 w-3 sm:h-4 sm:w-4 animate-spin mr-1 sm:mr-2" />
                                                {t('SAVING') || 'Saving...'}
                                            </>
                                        ) : (
                                            t('SAVE') || 'Save'
                                        )}
                                    </Button>
                                </div>
                            )}

                            <hr className="border-t border-gray-200" />
                        </div>
                    )}
                </div>

                {/* Memory Section */}
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg sm:text-xl font-semibold text-foreground">
                            {t('MEMORY') || 'Memory'}
                        </h2>
                        <button onClick={() => setIsMemoryDialogOpen(true)} className="text-[10px] sm:text-xs font-semibold text-foreground border border-gray-200 hover:border-primary rounded-full px-2 py-0.5 hover:bg-primary hover:text-background transition-colors">
                            {t('MANAGE') || 'Manage'}
                        </button>
                    </div>

                    <div className="flex flex-col">
                        <div className="flex items-start sm:items-center justify-between gap-3 sm:gap-4 py-3 sm:py-2 border-t border-t-gray-200">
                            <div className="flex flex-col gap-1 sm:gap-2 flex-1 min-w-0">
                                <h3 className="text-sm sm:text-base font-semibold">{t('REFERENCE_SAVED_MEMORIES') || 'Reference saved memories'}</h3>
                                <p className="text-xs sm:text-sm text-muted-foreground">{t('REFERENCE_SAVED_MEMORIES_DESCRIPTION') || 'Let SeliseAI save and use memories when responding.'}</p>
                            </div>
                            <Switch
                                className="w-[38px] sm:w-[42px] shrink-0"
                                id="enableSavedMemories"
                                checked={enableSavedMemories}
                                onCheckedChange={(checked) => handleSettingChange('savedMemories', checked)}
                            />
                        </div>

                        <div className="flex items-start sm:items-center justify-between gap-3 sm:gap-4 py-3 sm:py-2 border-t border-t-gray-200">
                            <div className="flex flex-col gap-1 sm:gap-2 flex-1 min-w-0">
                                <h3 className="text-sm sm:text-base font-semibold">{t('REFERENCE_CHAT_HISTORY') || 'Reference chat history'}</h3>
                                <p className="text-xs sm:text-sm text-muted-foreground">{t('REFERENCE_CHAT_HISTORY_DESCRIPTION') || 'Let SeliseAI reference all previous conversations when responding.'}</p>
                            </div>
                            <Switch
                                className="w-[38px] sm:w-[42px] shrink-0"
                                id="enableChatHistory"
                                checked={enableChatHistory}
                                onCheckedChange={(checked) => handleSettingChange('chatHistory', checked)}
                            />
                        </div>
                    </div>
                </div>

                <MemoryDialog isOpen={isMemoryDialogOpen} setIsOpen={setIsMemoryDialogOpen} />
            </div>
        </div>
    );
};